// Footsteps section — categorized team-wide projects
import { Eyebrow } from "./Pieces";
import { FOOTSTEPS, type AccentToken, type FootstepGroup, type FootstepItem } from "../data";

type Cluster =
  | { kind: "single"; group: FootstepGroup }
  | { kind: "parent"; label: string; subgroups: FootstepGroup[] };

export function FootstepsBlock() {
  const total = FOOTSTEPS.reduce((sum, g) => sum + g.items.length, 0);

  const clusters: Cluster[] = [];
  let currentParent: { kind: "parent"; label: string; subgroups: FootstepGroup[] } | null = null;
  for (const g of FOOTSTEPS) {
    if (g.parent) {
      if (!currentParent || currentParent.label !== g.parent) {
        currentParent = { kind: "parent", label: g.parent, subgroups: [] };
        clusters.push(currentParent);
      }
      currentParent.subgroups.push(g);
    } else {
      currentParent = null;
      clusters.push({ kind: "single", group: g });
    }
  }

  return (
    <div className="footsteps">
      <div className="footsteps-head">
        <div>
          <Eyebrow accent="point1">AIPQ의 발자취 · Footsteps</Eyebrow>
          <h3 className="footsteps-title">지금까지 만들어온 프로젝트를 소개합니다.</h3>
        </div>
        <div className="footsteps-meta">
          <div className="footsteps-count">
            <span className="footsteps-count-num">{total}</span>
            <span className="footsteps-count-label">projects</span>
          </div>
          <div className="footsteps-count">
            <span className="footsteps-count-num">{clusters.length}</span>
            <span className="footsteps-count-label">categories</span>
          </div>
        </div>
      </div>

      <div className="footsteps-groups">
        {clusters.map((c, ci) => {
          const displayIndex = ci - 1; // 'live' is headless, visible numbering starts at 01 from next
          if (c.kind === "single") {
            return (
              <FootstepsGroupView
                key={c.group.key}
                group={c.group}
                index={displayIndex}
                headless={c.group.key === "live"}
              />
            );
          }
          const count = c.subgroups.reduce((s, sg) => s + sg.items.length, 0);
          return (
            <ParentCluster
              key={c.label}
              label={c.label}
              index={displayIndex}
              subgroups={c.subgroups}
              count={count}
            />
          );
        })}
      </div>
    </div>
  );
}

function ParentCluster({
  label,
  index,
  subgroups,
  count,
}: {
  label: string;
  index: number;
  subgroups: FootstepGroup[];
  count: number;
}) {
  return (
    <section className="fs-parent accent-point1">
      <header className="fs-group-head fs-parent-head">
        <div className="fs-group-num">{String(index + 1).padStart(2, "0")}</div>
        <div className="fs-group-titlewrap">
          <div className="fs-group-tag accent-point1">AI-native capabilities</div>
          <h4 className="fs-group-title">{label}</h4>
        </div>
        <div className="fs-group-count">
          {count}개 프로젝트 · {subgroups.length}개 하위 분류
        </div>
      </header>
      <div className="fs-parent-subgroups">
        {subgroups.map((sg) => (
          <FootstepsSubgroup key={sg.key} group={sg} />
        ))}
      </div>
    </section>
  );
}

function FootstepsSubgroup({ group }: { group: FootstepGroup }) {
  return (
    <section className={`fs-subgroup accent-${group.accent}`}>
      <header className="fs-subgroup-head">
        <span className="fs-subgroup-bar" aria-hidden />
        <h5 className="fs-subgroup-title">{group.label}</h5>
      </header>
      <div className="fs-group-grid">
        {[...group.items]
          .sort((a, b) => (b.href ? 1 : 0) - (a.href ? 1 : 0))
          .map((it) => (
            <FootstepCard key={it.title} item={it} accent={group.accent} />
          ))}
      </div>
    </section>
  );
}

function FootstepsGroupView({
  group,
  index,
  headless,
}: {
  group: FootstepGroup;
  index: number;
  headless?: boolean;
}) {
  if (headless) {
    return (
      <section className={`fs-group accent-${group.accent} fs-group-live`}>
        <div className="fs-live-grid">
          {group.items.map((it) => (
            <LiveCard key={it.title} item={it} accent={group.accent} />
          ))}
        </div>
      </section>
    );
  }
  return (
    <section className={`fs-group accent-${group.accent}`}>
      <header className="fs-group-head">
        <div className="fs-group-num">{String(index + 1).padStart(2, "0")}</div>
        <div className="fs-group-titlewrap">
          <div className={`fs-group-tag accent-${group.accent}`}>{group.labelEn}</div>
          <h4 className="fs-group-title">{group.label}</h4>
        </div>
        <div className="fs-group-count">{group.items.length}개 프로젝트</div>
      </header>
      <div className="fs-group-grid">
        {[...group.items]
          .sort((a, b) => (b.href ? 1 : 0) - (a.href ? 1 : 0))
          .map((it) => (
            <FootstepCard key={it.title} item={it} accent={group.accent} />
          ))}
      </div>
    </section>
  );
}

function LiveCard({ item, accent }: { item: FootstepItem; accent: AccentToken }) {
  const isLink = !!item.href;
  const linkProps = isLink
    ? { href: item.href, target: "_blank" as const, rel: "noreferrer" }
    : {};
  const className = `live-card accent-${accent} ${isLink ? "is-link" : ""}`;
  const content = (
    <>
      <div className="live-card-hero">
        {item.screenshot ? (
          <img src={item.screenshot} alt={item.title} className="live-card-shot" loading="lazy" />
        ) : (
          <LiveCardPlaceholder title={item.title} />
        )}
        <div className="live-card-hero-overlay" aria-hidden />
      </div>
      <div className="live-card-body">
        <div className="live-card-head">
          <div className="live-card-title">{item.title}</div>
          <div className="live-card-status">
            {isLink ? (
              <span className="live-card-chip">Live</span>
            ) : (
              <span className="live-card-chip is-soon">Coming soon</span>
            )}
          </div>
        </div>
        <p className="live-card-summary">{item.summary}</p>
        {item.detail && <p className="live-card-detail">{item.detail}</p>}
        {item.url && (
          <div className="live-card-url">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            <span>{item.url}</span>
          </div>
        )}
      </div>
    </>
  );
  return isLink ? (
    <a className={className} {...linkProps}>
      {content}
    </a>
  ) : (
    <div className={className}>{content}</div>
  );
}

function LiveCardPlaceholder({ title }: { title: string }) {
  return (
    <div className="live-card-placeholder" aria-hidden>
      <div className="live-card-placeholder-grid">
        {Array.from({ length: 60 }).map((_, i) => (
          <span key={i} />
        ))}
      </div>
      <div className="live-card-placeholder-center">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="9" cy="9" r="1.5" />
          <path d="m21 15-5-5L5 21" />
        </svg>
        <div className="live-card-placeholder-label">스크린샷 준비 중</div>
        <div className="live-card-placeholder-title">{title}</div>
      </div>
    </div>
  );
}

function FootstepCard({ item, accent }: { item: FootstepItem; accent: AccentToken }) {
  const isLink = !!item.href;
  const linkProps = isLink
    ? { href: item.href, target: "_blank" as const, rel: "noreferrer" }
    : {};
  const className = `fs-card accent-${accent} ${isLink ? "is-link" : ""}`;
  const content = (
    <>
      <div className="fs-card-top">
        <div className="fs-card-dot" />
        <div className="fs-card-title">{item.title}</div>
        <div className="fs-card-status">
          {item.status && <span className="fs-status-chip">{item.status}</span>}
        </div>
      </div>
      <p className="fs-card-summary">{item.summary}</p>
      <p className="fs-card-detail">{item.detail || ""}</p>
      {isLink || item.url ? (
        <div className="fs-card-url">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
          <span>{item.url || (item.href || "").replace(/^https?:\/\//, "").replace(/\/$/, "")}</span>
        </div>
      ) : (
        <div className="fs-card-url fs-card-url-empty" aria-hidden />
      )}
    </>
  );
  return isLink ? (
    <a className={className} {...linkProps}>
      {content}
    </a>
  ) : (
    <div className={className}>{content}</div>
  );
}
